// Single source of truth for the dealership's physical locations — addresses,
// shared contact details and opening hours. Consumed by:
//   - useBusinessSchemaOrg (schema.org JSON-LD)
//   - the vehicle-detail print view (Kontakt block)
//
// IMPORTANT: the opening hours below are the machine-readable twin of the hours
// printed in `components/Footer/LowerFooter/index.vue`. If you change a
// location's hours in one place, change it in the other so they stay in sync.

// Shared contact details (dealership switchboard / info mailbox).
export const DEALER_TELEPHONE = '+43 3112 80100';
export const DEALER_EMAIL = 'hello@herbertseidl.at';

export type DealerOpeningHours = {
  /** schema.org day names, e.g. ['Monday', …, 'Friday'] */
  days: string[];
  opens: string;
  closes: string;
};

export type DealerLocation = {
  /** URL-fragment id, unique per location */
  id: string;
  /** Full name used in schema.org output */
  name: string;
  /** Short label for UI, e.g. "Standort Gleisdorf" */
  shortName: string;
  streetAddress: string;
  postalCode: string;
  addressLocality: string;
  openingHours: DealerOpeningHours[];
};

const MON_FRI = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export const DEALER_LOCATIONS: DealerLocation[] = [
  {
    id: 'gleisdorf',
    name: 'Herbert Seidl – Standort Gleisdorf',
    shortName: 'Standort Gleisdorf',
    streetAddress: 'Neugasse 102 - 103',
    postalCode: '8200',
    addressLocality: 'Gleisdorf',
    openingHours: [
      { days: MON_FRI, opens: '09:00', closes: '18:00' },
      { days: ['Saturday'], opens: '08:00', closes: '13:00' },
    ],
  },
  {
    id: 'gleisdorf-sued',
    name: 'Herbert Seidl – Standort Gleisdorf-Süd',
    shortName: 'Standort Gleisdorf-Süd',
    streetAddress: 'Hofstätten 221',
    postalCode: '8200',
    addressLocality: 'Gleisdorf',
    openingHours: [
      { days: MON_FRI, opens: '09:00', closes: '18:00' },
      { days: ['Saturday'], opens: '08:00', closes: '13:00' },
    ],
  },
  {
    id: 'oberwart',
    name: 'Herbert Seidl – Standort Oberwart',
    shortName: 'Standort Oberwart',
    streetAddress: 'Wiener Strasse 101',
    postalCode: '7400',
    addressLocality: 'Oberwart',
    openingHours: [
      { days: MON_FRI, opens: '09:00', closes: '18:00' },
      { days: ['Saturday'], opens: '08:00', closes: '12:00' },
    ],
  },
];

/**
 * Maps a vehicle's free-text `location` (e.g. "8200 Gleisdorf",
 * "8200 Hofstätten", "7400 Oberwart") to its dealership location.
 * Order matters: the Süd/Hofstätten site also contains the word "gleisdorf",
 * so it must be matched before the plain Gleisdorf site.
 */
export function resolveDealerLocation(location?: string | null): DealerLocation | undefined {
  if (!location) return undefined;
  const key = location.toLowerCase().trim();

  if (key.includes('oberwart')) return DEALER_LOCATIONS.find(l => l.id === 'oberwart');
  if (key.includes('hofstätten') || key.includes('süd')) return DEALER_LOCATIONS.find(l => l.id === 'gleisdorf-sued');
  if (key.includes('gleisdorf')) return DEALER_LOCATIONS.find(l => l.id === 'gleisdorf');

  return undefined;
}
